import {Directive, signal} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Province} from '../../models/province.model';
import {Municipality} from '../../models/municipality.model';
import {ProvinceService} from '../../../core/api/province.service';
import {MunicipalityService} from '../../../core/api/municipality.service';
import {ModalErrorService} from '../../../core/services/modal-error.service';
import {MrzFields} from '../../../core/services/mrz-fields.service';


@Directive()
export abstract class CheckInBaseComponent {
  form!: FormGroup;
  loading = signal(false);

  provinces: Province[] = [];
  municipalities: Municipality[] = [];

  protected constructor(
    protected fb: FormBuilder,
    protected provinceService: ProvinceService,
    protected municipalityService: MunicipalityService,
    protected modalErrorService: ModalErrorService,
  ) {
  }

  isFieldInvalid(fieldname: string): boolean {
    const field = this.form.get(fieldname);
    return !!field && field.invalid && (field.touched || field.dirty);
  }

  protected normalizeString(value: any): string {
    return String(value || '').trim();
  }

  protected normalizeUppercaseString(value: any): string {
    return this.normalizeString(value).toUpperCase();
  }

  protected isSpainNationalitySelected(): boolean {
    return this.normalizeUppercaseString(this.form.get('nacionalidad')?.value) === 'ESP';
  }

  protected isSpainSelected(): boolean {
    return this.normalizeUppercaseString(this.form.get('pais')?.value) === 'ESP';
  }

  protected updateAddressFieldsValidity(): void {
    this.form.get('provincia')?.updateValueAndValidity({emitEvent: false});
    this.form.get('municipio')?.updateValueAndValidity({emitEvent: false});
    this.form.get('estado')?.updateValueAndValidity({emitEvent: false});
    this.form.get('ciudad')?.updateValueAndValidity({emitEvent: false});
  }

  protected onNationalityChanged(): void {
    const apellido2Control = this.form.get('apellido2');
    if (this.isSpainNationalitySelected()) {
      apellido2Control?.setValidators([Validators.required]);
      apellido2Control?.updateValueAndValidity();
      return;
    }
    apellido2Control?.clearValidators();
    apellido2Control?.updateValueAndValidity();
  }

  protected onCountryChanged(): void {
    this.provinces = [];
    this.municipalities = [];

    this.form.patchValue({
      provincia: '',
      municipio: '',
      estado: '',
      ciudad: '',
    }, {emitEvent: false});

    if (!this.isSpainSelected()) {
      this.municipalityService.invalidateCache();
      return;
    }

    this.loadProvinces();
  }

  protected onProvinceChanged(province: string): void {
    if (!this.isSpainSelected()) {
      return;
    }

    this.municipalityService.invalidateCache();
    this.municipalities = [];
    this.form.patchValue({municipio: ''}, {emitEvent: false});

    if (!province) {
      return;
    }

    this.municipalityService.getMunicipalities(parseInt(province, 10)).subscribe({
      next: (municipalities) => this.municipalities = Array.isArray(municipalities) ? municipalities : [],
      error: (err) => {
        console.error('Error cargando municipios:', err);
        this.municipalities = [];
      }
    });
  }

  protected loadProvinces(): void {
    this.provinceService.getProvinces().subscribe({
      next: (provinces) => this.provinces = Array.isArray(provinces) ? provinces : [],
      error: (err) => {
        console.error('Error cargando provincias:', err);
        this.provinces = [];
      }
    });
  }

  protected getInvalidFieldMessages(): string[] {
    const fieldLabels: { [key: string]: string } = {
      nacionalidad: 'Nacionalidad',
      fechaNacimiento: 'Fecha de Nacimiento',
      nombre: 'Nombre',
      apellido1: 'Apellido 1',
      apellido2: 'Apellido 2',
      sexo: 'Género',
      tipoDocumento: 'Tipo de documento',
      numeroDocumento: 'Nº Documento',
      soporteDocumento: 'Soporte Documento',
      fechaExpedicion: 'Fecha de Expedición',
      pais: 'País',
      provincia: 'Provincia',
      municipio: 'Municipio',
      estado: 'Estado',
      ciudad: 'Ciudad',
      direccion: 'Dirección',
      codigoPostal: 'Código Postal',
      tipoContrato: 'Motivo de la estancia',
      email: 'Email',
    };

    const messages: string[] = [];

    Object.keys(fieldLabels).forEach((fieldName) => {
      const field = this.form.get(fieldName);
      if (!field || !field.invalid) return;

      if (field.hasError('required')) {
        messages.push(fieldLabels[fieldName] + ': campo obligatorio');
      } else if (field.hasError('invalidFormat')) {
        messages.push(fieldLabels[fieldName] + ': formato no válido');
      } else if (field.hasError('invalidDni')) {
        messages.push('Nº Documento: el DNI no es válido');
      } else if (field.hasError('invalidNie')) {
        messages.push('Nº Documento: el NIE no es válido');
      } else if (field.hasError('invalidPassport')) {
        messages.push('Nº Documento: el pasaporte no tiene un formato válido');
      } else if (field.hasError('invalidDocument')) {
        messages.push('Nº Documento: el documento no tiene un formato válido');
      }
    });

    return messages;
  }

  protected applyMrzToForm(mrz: MrzFields): void {
    let apellido1 = '';
    let apellido2 = '';

    if (mrz['lastName'] && mrz['lastName'].trim().length > 0) {
      const apellidos = mrz['lastName'].split(' ').filter((a: string) => a.trim().length > 0);
      apellido1 = apellidos[0] ?? '';
      apellido2 = apellidos.slice(1).join(' ');
    }

    let birthDate = this.toHumanReadableBirthDateDate(mrz['birthDate'] ?? '');
    let expiryDate = this.toHumanReadableExpiryDateDate(mrz['expirationDate'] ?? '');
    let expeditionDate = '';
    if (expiryDate.length > 0  && !isNaN(Date.parse(expiryDate).valueOf())) {
      const date = new Date(expiryDate);
      date.setFullYear(date.getFullYear() - 10);
      expeditionDate = date.toISOString().split('T')[0];
    }
    let sexo = mrz['sex'] ?? '';
    if (sexo === 'male') sexo = 'M';
    else if (sexo === 'female') sexo = 'F';
    else sexo = '';

    const tipoDocumento = this.extractTipoDocumento(mrz['documentCode']);
    const numeroDocumento = tipoDocumento === 'P' || tipoDocumento === 'I' ? (mrz['documentNumber'] ?? '') : (mrz['optional1'] ?? '');
    const soporteDocumento = this.shouldShowSoporteDocumento(tipoDocumento) ? (mrz['documentNumber'] ?? '') : '';

    this.form.patchValue({
      nacionalidad: (mrz['nationality'] ?? '').toLowerCase(),
      fechaNacimiento: birthDate,
      nombre: mrz['firstName'] ?? '',
      apellido1: apellido1 ?? '',
      apellido2: apellido2,
      sexo: sexo,
      tipoDocumento: tipoDocumento,
      numeroDocumento: numeroDocumento,
      soporteDocumento: soporteDocumento,
      fechaCaducidad: expiryDate,
      fechaExpedicion: expeditionDate,
    });
  }

  protected shouldShowSoporteDocumento(tipoDocumento: string): boolean {
    return tipoDocumento === 'D' || tipoDocumento === 'N';
  }

  protected extractTipoDocumento(tipoDocumento: string): string {
    if (!tipoDocumento || tipoDocumento.length > 2) {
      return '';
    }

    if (tipoDocumento === 'ID') return 'D';
    if (tipoDocumento === 'IR') return 'N';
    if (tipoDocumento === 'P') return 'P';

    return 'I';
  }

  protected toHumanReadableBirthDateDate(birthDate: string): string {
    if (!birthDate || birthDate.length !== 6) {
      return '';
    }

    const yy = birthDate.substring(0, 2);
    const mm = birthDate.substring(2, 4);
    const dd = birthDate.substring(4, 6);

    const currentYearLastTwoDigits = new Date().getFullYear() % 100;
    const year = parseInt(yy, 10) > currentYearLastTwoDigits ? `19${yy}` : `20${yy}`;

    return `${year}-${mm}-${dd}`;
  }

  protected toHumanReadableExpiryDateDate(expiryDate: string): string {
    if (!expiryDate || expiryDate.length !== 6) {
      return '';
    }

    const yy = expiryDate.substring(0, 2);
    const mm = expiryDate.substring(2, 4);
    const dd = expiryDate.substring(4, 6);

    const currentYearLastTwoDigits = new Date().getFullYear() % 100;
    const year = parseInt(yy, 10) >= currentYearLastTwoDigits ? `20${yy}` : `21${yy}`;

    return `${year}-${mm}-${dd}`;
  }
}
